import React from 'react';
import PropTypes from 'prop-types';
import { get, orderBy } from 'lodash';
import classNames from 'classnames';

import { Icon } from 'web-ui-components/lib/atoms/iconography';
import { LoadingLocalTakeover } from 'web-ui-components/lib/organisms/takeovers';
import { Identifier } from 'web-ui-components/lib/typography/modifiers';
import { ActionButton } from 'web-ui-components/lib/actions/buttons';
import { NavigationButton } from 'web-ui-components/lib/navigation/buttons';

import { ToolTipComponent } from '../../common/ToolTipComponent';
import PaymentHistorySMEStore from '../../../stores/PaymentHistorySMEStore';
import AccountsStore from '../../../stores/AccountsStore';
import UserManagementStore from '../../../stores/UserManagementStore';
import {
  getPaymentHistoryList,
  updateAccount,
  updateStatus,
  updateDateInterval,
  updateFromDate,
  updateToDate,
  showMore,
  updateSortOrder,
  updateSearch,
} from '../../../actions/PaymentsHistorySMEActionCreator';
import ViewPaymentHistory from '../ViewPaymentHistory';
import ViewPaymentBatchHistory from './ViewBatchPaymentHistory';
import StatusBadgeSME from './StatusBadgeSME';
import StringConstants, {
  paymentTypes,
  CONST_BATCH_TYPE_UPLOAD,
} from '../../../constants/StringConstants';
import PaymentHistorySMEDetail from './PaymentHistorySMEDetail';
import PaymentAccountDropDown from './PaymentAccountDropDown';
import FavouritesStore from '../../../stores/AccountsStore/FavouritesStore';
import DetermineAccountPicker from '../../managepayments/DetermineAccountPicker';

export const getColumns = ({ sortBy, sortOrder }) => {
  const columns = [
    {
      title: 'Date',
      id: 'createdDateRaw',
      path: ['createdDate'],
      sortDirection: 'asc',
    },
    {
      title: 'From',
      id: 'fromAccountName',
      path: ['fromAccountName'],
      sortDirection: 'asc',
    },
    {
      title: 'To',
      id: 'toAccountName',
      path: ['toAccountName'],
      sortDirection: 'asc',
    },
    {
      title: 'Amount',
      id: 'amountRaw',
      path: ['amount'],
      alignment: 'right',
      sortDirection: 'asc',
    },
    {
      title: 'Type',
      id: 'paymentType',
      path: ['paymentType'],
      alignment: 'left',
      sortDirection: 'asc',
    },
    {
      title: 'Status',
      id: 'status',
      path: ['statusBadge'],
    },
    {
      title: 'Options',
      id: 'options',
      path: ['options'],
    },
  ];

  const sortedColumn = columns.filter((column) => column.id === sortBy);
  sortedColumn[0].sortDirection = sortOrder;
  return columns;
};
const isRelevant = (payment, search, isBatch) => {
  const isFound =
    get(payment, 'toAccountName', '').toLowerCase().indexOf(search) > -1;
  if (isBatch) {
    return (
      isFound ||
      get(payment, 'fromAccountName', '').toLowerCase().indexOf(search) > -1
    );
  }
  return isFound;
};

const searchInBatch = (batchPayments = [], search) =>
  batchPayments.some((payment) => isRelevant(payment, search, true));

export const getFilteredResults = (allTransactions = [], search = '') => {
  if (search.length > 2) {
    const searchLowered = search.toLowerCase();
    return allTransactions.filter(
      (transaction) =>
        isRelevant(transaction, searchLowered) ||
        searchInBatch(transaction.children, searchLowered)
    );
  }

  return allTransactions;
};

export const formatFromName = (name, sortCode, accountNumber, account_uuid) => {
  let account = AccountsStore.getAccountByAccountNumberSortCode(
    accountNumber,
    sortCode
  );
  if (account && account.displayName) return account.displayName;
  account = AccountsStore.getAccountDetail(account_uuid);
  if (account && account.displayName) return account.displayName;
  if (name) return name;
  if (sortCode) return `${sortCode} | ${accountNumber}`;
  return accountNumber;
};

export const formatFromNameTableView = (
  accountHolder,
  name,
  sortCode,
  accountNumber
) => {
  if (sortCode && name)
    return (
      <div>
        {accountHolder && (
          <div>
            <small>{accountHolder}</small>
          </div>
        )}
        <div>
          <strong>{name}</strong>
        </div>
        <div>
          <small>{`${sortCode} | ${accountNumber}`}</small>
        </div>
      </div>
    );
  if (name) return name;
  if (sortCode) return `${sortCode} | ${accountNumber}`;
  return accountNumber;
};

export const formatToName = (name, sortCode, accountNumber) => {
  if (name) return name;
  if (sortCode) return `${sortCode} | ${accountNumber}`;
  return accountNumber;
};

export const isBatch = (transaction) =>
  !!transaction.children && transaction.children.length > 0;

export const formatTransaction = (transaction, handleView) => {
  return {
    ...transaction,
    createdDate: transaction.createdDate,
    fromAccountName: formatFromNameTableView(
      get(transaction, 'fromAccount.accountHolder', ''),
      transaction.fromAccountName,
      transaction.fromAccountSortCode,
      transaction.fromAccountAccountNumber,
      transaction.fromAccountAccountUUID
    ),
    toAccountName: (
      <Identifier.Breaking>
        {formatToName(
          transaction.toAccountName,
          transaction.toAccountSortCode,
          transaction.toAccountAccountNumber
        )}
      </Identifier.Breaking>
    ),
    options: (
      <NavigationButton.Tertiary
        labelText="View"
        onClick={() => handleView(transaction)}
        data-test-id={`view-button-${transaction.id}`}
      />
    ),
    statusBadge: <StatusBadgeSME status={transaction.status} />,
    paymentType: transaction.paymentType,
  };
};

export const getBatchOption = (
  batch,
  isExpanded,
  handleViewAll,
  handleHideAll
) => (
  <NavigationButton.Tertiary
    labelText={isExpanded ? 'Hide All' : 'View All'}
    onClick={() => (isExpanded ? handleHideAll(batch) : handleViewAll(batch))}
    data-test-id={
      isExpanded ? `hide-button-${batch.id}` : `show-button-${batch.id}`
    }
  />
);

export const formatBatch = (
  batch,
  isExpanded,
  handleView,
  handleViewAll,
  handleHideAll
) => {
  const result = [];
  result.push({
    ...batch,
    createdDate: batch.createdDate,
    fromAccountName:
      batch.batchType === CONST_BATCH_TYPE_UPLOAD
        ? 'File upload summary'
        : formatFromNameTableView(
            get(batch, 'fromAccount.accountHolder', ''),
            batch.fromAccountName,
            batch.fromAccountSortCode,
            batch.fromAccountAccountNumber,
            batch.fromAccountAccountUUID
          ),
    toAccountName: (
      <Identifier.Breaking>
        {formatFromName(
          batch.toAccountName,
          batch.toAccountSortCode,
          batch.toAccountAccountNumber,
          batch.toAccountAccountUUID
        )}
      </Identifier.Breaking>
    ),
    options: getBatchOption(batch, isExpanded, handleViewAll, handleHideAll),
    statusBadge: <StatusBadgeSME status={batch.status} />,
  });
  if (isExpanded) {
    batch.children.forEach((item) => {
      result.push({
        id: item.id,
        amount: item.amount,
        ...(batch.type !== paymentTypes.TYPE_BACS_DEBIT && {
          toAccountName: (
            <Identifier.Breaking>
              {formatToName(
                item.toAccountName,
                item.toAccountSortCode,
                item.toAccountAccountNumber
              )}
            </Identifier.Breaking>
          ),
        }),
        ...((batch.type === paymentTypes.TYPE_BACS_DEBIT ||
          batch.batchType === CONST_BATCH_TYPE_UPLOAD) && {
          fromAccountName: formatToName(
            item.fromAccountName,
            item.fromAccountSortCode,
            item.fromAccountAccountNumber
          ),
        }),
        options: (
          <NavigationButton.Tertiary
            labelText="View"
            onClick={() => handleView(item)}
            data-test-id={`view-button-${item.id}`}
          />
        ),
        statusBadge: item.status && <StatusBadgeSME status={item.status} />,
      });
    });
  }
  return result;
};

export const getAdditionalHistoryDetails = (id, paymentHistoryList) => {
  const searchInBatch = (batchPayments = [], id) => {
    return batchPayments.some((payment) => payment.id === id);
  };

  const payment = paymentHistoryList.find((transaction) => {
    return transaction.id === id || searchInBatch(transaction.children, id);
  });
  return payment;
};

const getSelectedAccountFromId = (id) =>
  id === StringConstants.ALL_ACCOUNTS
    ? undefined
    : FavouritesStore.getAccountDetail(id);

class PaymentHistorySME extends React.Component {
  constructor(props) {
    super(props);
    const filterCriteria = PaymentHistorySMEStore.getFilterCriteria();
    this.state = {
      filterCriteria,
      selectedAccountId: filterCriteria.accountId,
      paymentHistoryList: PaymentHistorySMEStore.paymentHistoryList(),
      isLoading: PaymentHistorySMEStore.isLoading(),
      hasError: PaymentHistorySMEStore.hasError(),
      refreshError: false,
      showHistoryDetails: false,
      openBatches: [],
      isLargeCustomerView: FavouritesStore.isLargeCustomer(),
    };
    this.onStoreChange = this.onStoreChange.bind(this);
    this.handleChangeAccount = this.handleChangeAccount.bind(this);
    this.renderFooter = this.renderFooter.bind(this);
    this.renderHistory = this.renderHistory.bind(this);
    this.handlePaymentStatusSelect = this.handlePaymentStatusSelect.bind(this);
    this.handleDateIntervalChange = this.handleDateIntervalChange.bind(this);
    this.handleToDateChange = this.handleToDateChange.bind(this);
    this.handleFromDateChange = this.handleFromDateChange.bind(this);
    this.handleView = this.handleView.bind(this);
    this.handleViewBack = this.handleViewBack.bind(this);
    this.handleViewAll = this.handleViewAll.bind(this);
    this.handleHideAll = this.handleHideAll.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.getResultsToDisplay = this.getResultsToDisplay.bind(this);
  }

  componentWillMount() {
    PaymentHistorySMEStore.addChangeListener(this.onStoreChange);
    if (!FavouritesStore.isLargeCustomer()) {
      getPaymentHistoryList();
    }
  }
  componentWillUnmount() {
    PaymentHistorySMEStore.removeChangeListener(this.onStoreChange);
  }

  onStoreChange() {
    this.setState({
      filterCriteria: PaymentHistorySMEStore.getFilterCriteria(),
      paymentHistoryList: PaymentHistorySMEStore.paymentHistoryList(),
      hasError: PaymentHistorySMEStore.hasError(),
      isLoading: PaymentHistorySMEStore.isLoading(),
    });
  }

  handleFromDateChange(fromDate) {
    updateFromDate(fromDate);
  }
  handleToDateChange(toDate) {
    updateToDate(toDate);
  }
  handlePaymentStatusSelect(status) {
    updateStatus(status);
  }
  handleDateIntervalChange(dateInterval, fromDate) {
    const isCustomDateInterval = dateInterval === 'Custom Date';
    updateDateInterval({
      isCustomDateInterval,
      dateInterval,
      fromDate,
    });
  }
  getResultsToDisplay() {
    const {
      paymentHistoryList,
      filterCriteria: { numberToDisplay, sortBy, sortOrder, search },
    } = this.state;
    const filterTransactions = orderBy(
      getFilteredResults(paymentHistoryList, search),
      [sortBy],
      [sortOrder]
    );
    const pagedTransactions =
      filterTransactions.length > numberToDisplay
        ? filterTransactions.slice(0, numberToDisplay)
        : filterTransactions;

    const results = [];
    pagedTransactions.forEach((transaction) => {
      if (isBatch(transaction)) {
        const isExpanded = this.state.openBatches.indexOf(transaction.id) > -1;
        results.push(
          ...formatBatch(
            transaction,
            isExpanded,
            this.handleView,
            this.handleViewAll,
            this.handleHideAll
          )
        );
      } else {
        results.push(formatTransaction(transaction, this.handleView));
      }
    });

    return results;
  }

  handleChangeAccount(accountId) {
    this.setState(
      {
        selectedAccountId: accountId,
      },
      updateAccount(accountId)
    );
  }

  handleSortChange(column) {
    const defaultSortOrder = {
      createdDateRaw: 'desc',
      fromAccountName: 'asc',
      toAccountName: 'asc',
      amountRaw: 'asc',
      paymentType: 'asc',
    };

    let sortOrder = defaultSortOrder[column.id];
    if (!sortOrder) {
      return;
    }

    if (this.state.filterCriteria.sortBy === column.id) {
      const currentSortOrder = this.state.filterCriteria.sortOrder;
      sortOrder = currentSortOrder === 'asc' ? 'desc' : 'asc';
    }

    updateSortOrder(column.id, sortOrder);
  }

  handleViewAll(batch) {
    this.setState((prevState) => ({
      openBatches: [...prevState.openBatches, batch.id],
    }));
  }

  handleHideAll(batch) {
    this.setState((prevState) => ({
      openBatches: prevState.openBatches.filter(
        (batchId) => batchId !== batch.id
      ),
    }));
  }

  handleView(transaction) {
    const { handleIsListing } = this.props;
    this.setState(
      {
        showHistoryDetails: true,
        showDetailsOf: transaction,
        isBatch: false,
      },
      handleIsListing(false)
    );
  }
  handleViewBack() {
    const { handleIsListing } = this.props;
    this.setState(
      {
        showHistoryDetails: false,
      },
      handleIsListing(true)
    );
  }
  handleSearchChange(searchTerm) {
    updateSearch(searchTerm);
  }

  renderFooter() {
    const { content } = this.props;
    const { numberToDisplay, search } = this.state.filterCriteria;
    const noOfPaymentHistory = getFilteredResults(
      this.state.paymentHistoryList,
      search
    ).length;
    const isShowMore = numberToDisplay < noOfPaymentHistory;
    const isPaymentHistoryEmpty = noOfPaymentHistory === 0;
    if (isShowMore) {
      return (
        <ActionButton.Primary
          data-test-id="showMoreBtn"
          onClick={showMore}
          labelText={content.showMore}
        />
      );
    }
    if (search.length && isPaymentHistoryEmpty) {
      return <p>{content.searchPaymentHistoryEmptyMsg}</p>;
    }
    return (
      <p>
        {isPaymentHistoryEmpty
          ? content.paymentHistoryNoTransactionMsg
          : content.paymentHistoryFooterMsg}
      </p>
    );
  }
  renderDetails() {
    const { showDetailsOf, paymentHistoryList, isBatch } = this.state;
    const { content } = this.props;
    const paymentHistoryDetails = {
      ...getAdditionalHistoryDetails(showDetailsOf.id, paymentHistoryList),
      ...showDetailsOf,
    };
    const historyToView = {
      ...paymentHistoryDetails,
    };

    const ViewHistoryComponent =
      isBatch && UserManagementStore.isSMEUser()
        ? ViewPaymentBatchHistory
        : ViewPaymentHistory;

    return (
      <ViewHistoryComponent
        paymentHistoryDetails={historyToView}
        goBack={this.handleViewBack}
        content={content}
        isSMEUser
      />
    );
  }
  renderHistory() {
    const { accountList, content, onShowAccountPicker } = this.props;
    const {
      paymentHistoryListHeader,
      paymentHistoryToolTipMessage,
      accountPickerLabel,
    } = content;

    const {
      filterCriteria,
      isLoading,
      selectedAccountId,
      isLargeCustomerView,
    } = this.state;

    const results = this.getResultsToDisplay();
    const columns = getColumns(filterCriteria);

    const svgimage = (
      <a
        href="javascript:;"
        className="tooltip-icon"
        aria-label="Information"
        role="button"
      >
        <Icon iconType="InfoOutlineLarge" size="sm" />
      </a>
    );

    const gridClasses = classNames('payment-history-grid', {
      'component-has-global-takeover': isLoading,
    });

    return (
      <div>
        <section className="u-flex payment-history-header web-ui-components">
          <h3 className="header-title">{paymentHistoryListHeader}&nbsp;</h3>
          <div className="header__info-icon">
            <ToolTipComponent
              className="info-icon"
              hasSvg
              trigger="manual"
              childComponent={svgimage}
              placement="bottom"
              popoverChildComponent={
                <p
                  dangerouslySetInnerHTML={{
                    __html: paymentHistoryToolTipMessage,
                  }}
                />
              }
            />
          </div>
        </section>
        <LoadingLocalTakeover show={isLoading} title="loading...">
          <div className="payment-history">
            <DetermineAccountPicker
              isLargeCustomerView={isLargeCustomerView}
              selectedAccount={getSelectedAccountFromId(selectedAccountId)}
              numberOfAccounts={accountList.length}
              onChooseAccount={onShowAccountPicker}
              content={content}
              renderDropDown={() => (
                <PaymentAccountDropDown
                  label={accountPickerLabel}
                  selectedAccountId={selectedAccountId}
                  accountList={accountList}
                  onChangeAccount={this.handleChangeAccount}
                />
              )}
              chooseAccountText={content.accountPickerPaymentsChoose}
            />

            {(!isLargeCustomerView ||
              (isLargeCustomerView &&
                selectedAccountId !== StringConstants.ALL_ACCOUNTS)) && (
              <PaymentHistorySMEDetail
                onDateIntervalChange={this.handleDateIntervalChange}
                onFromDateChange={this.handleFromDateChange}
                onToDateChange={this.handleToDateChange}
                onPaymentStatusSelect={this.handlePaymentStatusSelect}
                onSortChange={this.handleSortChange}
                onSearchChange={this.handleSearchChange}
                filterCriteria={filterCriteria}
                gridClasses={gridClasses}
                columns={columns}
                results={results}
                renderFooter={this.renderFooter}
                content={this.props.content}
              />
            )}
          </div>
        </LoadingLocalTakeover>
      </div>
    );
  }

  render() {
    const { showHistoryDetails } = this.state;
    return showHistoryDetails ? this.renderDetails() : this.renderHistory();
  }
}

PaymentHistorySME.propTypes = {
  handleIsListing: PropTypes.func.isRequired,
  content: PropTypes.shape({
    showMore: PropTypes.string.isRequired,
    accountPickerLabel: PropTypes.string.isRequired,
    paymentHistoryListHeader: PropTypes.string.isRequired,
    paymentHistoryToolTipMessage: PropTypes.string.isRequired,
    paymentHistoryFooterMsg: PropTypes.string.isRequired,
    paymentHistoryLoadingMessage: PropTypes.string.isRequired,
    searchPaymentHistoryEmptyMsg: PropTypes.string.isRequired,
  }),
  handleBack: PropTypes.func.isRequired,
  accountList: PropTypes.arrayOf(PropTypes.shape({})),
  onShowAccountPicker: PropTypes.func.isRequired,
};

export default PaymentHistorySME;
